import { Dispatch, useMemo, useState } from 'react';
import { Checkbox } from './checkbox';
import Modal from './Modal';
import { type LabelValue } from '@/types/global';
import { useTranslation } from '@/i18n';
import { Label } from './label';

interface ModalSuppressionProps {
  closeModal: () => void;
  actionOnClick: (selectedFields: string[]) => void;
  fields: Array<LabelValue<string>>;
  code: number;
  otherFields?: Array<LabelValue<string>>;
  fieldsTitle?: string;
  otherFieldsTitle?: string;
  type?: 'fournisseur' | 'prestation' | 'circuit';
}

const ModalDuplication = ({
  closeModal,
  actionOnClick,
  fields,
  code,
  otherFields,
  fieldsTitle,
  otherFieldsTitle,
  type = 'fournisseur',
}: ModalSuppressionProps) => {
  const { t } = useTranslation();
  const title = useMemo(() => {
    if (type === 'fournisseur') return t('modal.duplication.title.fournisseur');
    if (type === 'prestation') return t('modal.duplication.title.prestation');
    return t('modal.duplication.title.circuit');
  }, [type, t]);

  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [selectedOtherFields, setSelectedOtherFields] = useState<string[]>([]);

  return (
    <Modal
      actionOnClick={() =>
        actionOnClick([...selectedFields, ...selectedOtherFields])
      }
      actionTitle={t('modal.duplication.actionTitle')}
      closeModal={closeModal}
      title={`${title}${code.toString().padStart(4, '0')}`}
      message={t('modal.duplication.message')}
    >
      <div className="flex flex-row w-full gap-4">
        <FieldsCheckboxesCol
          fields={fields}
          selectedFields={selectedFields}
          setSelectedFields={setSelectedFields}
          title={fieldsTitle}
        />
        {otherFields && otherFields.length > 0 && (
          <FieldsCheckboxesCol
            fields={otherFields}
            selectedFields={selectedOtherFields}
            setSelectedFields={setSelectedOtherFields}
            title={otherFieldsTitle}
          />
        )}
      </div>
    </Modal>
  );
};

export default ModalDuplication;

const FieldsCheckboxesCol = ({
  fields,
  selectedFields,
  setSelectedFields,
  title,
}: {
  fields: Array<LabelValue<string>>;
  selectedFields: string[];
  setSelectedFields: Dispatch<React.SetStateAction<string[]>>;
  title?: string;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3 w-full">
      <h2>{title}</h2>
      <div className="flex items-center gap-2">
        <Checkbox
          checked={selectedFields.length === fields.length}
          onCheckedChange={() =>
            setSelectedFields((prevSelectedFields) =>
              prevSelectedFields.length === fields.length
                ? []
                : fields.map((f) => f.value),
            )
          }
          id={`duplicateAll-${title}`}
        />
        <Label
          className="text-sm leading-6 font-medium text-gray-700"
          htmlFor={`duplicateAll-${title}`}
        >
          {t('modal.duplication.duplicateAll')}
        </Label>
      </div>
      {fields.map((field) => (
        <div key={field.value} className="flex items-center gap-2">
          <Checkbox
            checked={selectedFields.includes(field.value)}
            onCheckedChange={() =>
              setSelectedFields((prevSelectedFields) =>
                prevSelectedFields.includes(field.value)
                  ? prevSelectedFields.filter((f) => f !== field.value)
                  : [...prevSelectedFields, field.value],
              )
            }
            id={field.value}
          />
          <Label
            className="text-sm leading-6 font-medium text-gray-700"
            htmlFor={field.value}
          >
            {field.label}
          </Label>
        </div>
      ))}
    </div>
  );
};
